import React from 'react';
import {useForm} from 'react-hook-form';
import {graphql, useMutation, useLazyLoadQuery} from 'react-relay/hooks';
import useMountedState from '../../useMountedState';
import PopUp from '../pop-up/pop-up.js';
import Button from '../button/button';
import UseFormTextarea from '../use-form-textarea/use-form-textarea';
import {useAuthorizationState} from '../../contexts/Authorization';
import {graphqlWhoamiNextQuery} from '../graphql';

const RAZZLE_GRECAPTCHA = process.env.RAZZLE_GRECAPTCHA;

const feedbackMutation = graphql`
    mutation feedbackMutation($request: ApplyRequestInput) {
        apply(request: $request) {
            email
        }
    }
`;

const FeedbackImpl = React.memo(({children, userEmail}) => {
    const [feedbackPopUpVisible, setFeedbackPopUpVisible] = React.useState(false);
    const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);
    const [feedbackLoading, setFeedbackLoading] = React.useState(false);
    const {register, handleSubmit, formState: {errors}, reset} = useForm();
    const [commit, isMutationInFlight] = useMutation(feedbackMutation);
    const [grecaptcha, setGrecaptcha] = useMountedState(false);
    const onFeedbackClick = React.useCallback((e) => { 
        e.preventDefault(); 
        setFeedbackPopUpVisible(true);
        setFeedbackSubmitted(false);
        reset();
    }, [reset]);
    const onFeedbackSubmit = React.useCallback((data) => {
        setFeedbackLoading(true);
        setGrecaptcha(true);
        window.grecaptcha.ready(() => {
            window.grecaptcha.execute(RAZZLE_GRECAPTCHA, {action: 'submit'}).then((token) => {
                commit({
                    variables: { request: { email: userEmail, name: '1', phone: '2', event: ['0'], payment: ['0'], sale: ['0'], comment: data.feedback, reCaptcha: token } },
                    onCompleted: () => {
                        setFeedbackSubmitted(true);
                        setFeedbackLoading(false);
                        setGrecaptcha(false);
                    },
                    onError: () => {
                        setFeedbackLoading(false);
                        setGrecaptcha(false);
                    }
                });
            });
        });
    }, [commit, userEmail]);
    const onNewFeedback = React.useCallback(() => {
        setFeedbackSubmitted(false);
        reset();
    }, [reset]);
    return (
        <>
            <div className='cursor-pointer' onClick={onFeedbackClick}>
                {children}
            </div>
            <PopUp 
                {...{
                    mode: 'default', 
                    isVisible: feedbackPopUpVisible, 
                    icon: 'message', 
                    title: 'Ваш відгук?', 
                    message: 'Ваш відгук — наше натхнення для змін на краще. Від дрібних деталей до масштабних оновлень!',
                    content: !feedbackSubmitted ? (
                        <>
                            <form onSubmit={handleSubmit(onFeedbackSubmit)} className='width-100percent text-align-start'>
                                <UseFormTextarea
                                    label='Повідомлення '
                                    fieldname='feedback'
                                    register={register}
                                    errors={errors.feedback}
                                    registerOptions={{required: true}}
                                    rows={4}
                                    placeholder='Розкажіть про ваш досвід, побажання або зауваження...'
                                />
                            </form>
                        </>
                    ) : (
                        <div className='text-center'>
                            <p className='text-lg color-success-600'>Дякуємо за ваш відгук!</p>
                        </div>
                    )
                }}
            >
                {!feedbackSubmitted ? (
                    <>
                        <div className='flex-1'>
                            <Button {...{color: 'primary', size: 'lg', fluid: 'always', clickHandler: handleSubmit(onFeedbackSubmit), isLoading: feedbackLoading || grecaptcha}}>
                                Надіслати
                            </Button>
                        </div>
                        <div className='flex-1'>
                            <Button {...{color: 'secondary-gray', size: 'lg', fluid: 'always', clickHandler: () => setFeedbackPopUpVisible(false)}}>
                                Повернутися
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className='flex-1'>
                            <Button {...{color: 'primary', size: 'lg', fluid: 'always', clickHandler: () => setFeedbackPopUpVisible(false)}}>
                                Повернутися
                            </Button>
                        </div>
                        <div className='flex-1'>
                            <Button {...{color: 'secondary-gray', size: 'lg', fluid: 'always', clickHandler: onNewFeedback}}>
                                Ще відгук
                            </Button>
                        </div>
                    </>
                )}
            </PopUp>
        </>
    );
});

const AuthenticatedFeedback = React.memo(({children}) => {
    const {whoamiNext} = useLazyLoadQuery(graphqlWhoamiNextQuery);
    const userEmail = whoamiNext?.username || whoamiNext?.email || 'feedback@ottry.com';
    return <FeedbackImpl userEmail={userEmail}>{children}</FeedbackImpl>;
});

const UnauthenticatedFeedback = React.memo(({children}) => {
    const userEmail = 'feedback@ottry.com';
    return <FeedbackImpl userEmail={userEmail}>{children}</FeedbackImpl>;
});

const Feedback = ({children}) => {
    const authorized = useAuthorizationState();
    if (authorized) {
        return <AuthenticatedFeedback>{children}</AuthenticatedFeedback>;
    } else {
        return <UnauthenticatedFeedback>{children}</UnauthenticatedFeedback>;
    }
};

export default React.memo(Feedback);
